import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllInternalStates } from 'services/internalStatusServices';
import {
  getDocumentLink,
  saveManagement,
} from 'services/unholsterDailyStateService';
import useUnholsterCaseMovements from 'hooks/useUnholsterCaseMovements';
import usePermissionsCauses from '../../../../permissions/modules/causes/usePermissionsCauses';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { formatDateByLocale } from '../../../../utils/formatDate';
import { useSnackbar } from 'context/SnackbarContext';
import { caseMovementsUnholsterI18n } from './i18n';

const PAGE_SIZES = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

// El catalogo guarda 'info' para No amerita; la vista lo pinta rojo, igual que Estado diario.
const COLOR_GESTION = { 1: 'warning', 2: 'success', 3: 'error' };
const PENDIENTE = 1;

const CaseMovementsUnholster = () => {
  const { DesignSystemComponents, caseData } = useOutletContext();
  const { id } = useParams();
  const { language } = useLanguage();
  const locale = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: caseMovementsUnholsterI18n });
  const { showSnackbar } = useSnackbar();

  const { Table, Badge, IconButton, Dropdown, SvgIcon, Button } =
    DesignSystemComponents;

  const { canViewDocument, canManageMovements } = usePermissionsCauses();

  const { data, meta, isLoading, error, fetchMovements, patchRow } =
    useUnholsterCaseMovements();

  const [statusCatalog, setStatusCatalog] = useState([]);
  const [savingRows, setSavingRows] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const caseId = caseData?.id ?? Number(id);

  useEffect(() => {
    caseMovementsUnholsterI18n.changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    if (!caseId) return;
    fetchMovements(caseId);
  }, [caseId, fetchMovements]);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const statuses = await getAllInternalStates();
        setStatusCatalog(Array.isArray(statuses) ? statuses : []);
      } catch {
        // Sin catalogo la tabla se lee igual, solo no se puede gestionar.
      }
    };

    loadCatalog();
  }, []);

  const statusOptions = useMemo(
    () =>
      statusCatalog.map((s) => ({
        label: s.name,
        value: s.id,
        color: s.badgeColor,
      })),
    [statusCatalog]
  );

  const statusBadge = useCallback(
    (row) => {
      const statusId = row.internalStatusId;
      if (!statusId)
        return { label: t('cmu_state_pending'), color: COLOR_GESTION[PENDIENTE] };

      const found = statusCatalog.find((s) => s.id === statusId);
      return {
        label: row.internalStatusNombre || found?.name || t('cmu_state_pending'),
        color: COLOR_GESTION[statusId] ?? 'default',
      };
    },
    [statusCatalog, t]
  );

  const openDocument = useCallback(
    async (documentoId) => {
      try {
        const link = await getDocumentLink(documentoId);

        if (!link?.disponible) {
          showSnackbar(t('cmu_document_unavailable'), 'warning');
          return;
        }

        window.open(link.url, '_blank', 'noopener,noreferrer');
      } catch {
        showSnackbar(t('cmu_document_error'), 'error');
      }
    },
    [showSnackbar, t]
  );

  const handleManage = useCallback(
    async (selected, row) => {
      const key = row.estadoDiarioId;
      if (savingRows.has(key)) return;

      const previo = {
        internalStatusId: row.internalStatusId,
        internalStatusNombre: row.internalStatusNombre,
      };

      setSavingRows((prev) => new Set(prev).add(key));
      patchRow(key, {
        internalStatusId: selected.value,
        internalStatusNombre: selected.label,
      });

      try {
        await saveManagement({
          caseId: row.caseId,
          rol: row.rol,
          codTribunal: row.codTribunal,
          fecha: row.fecha,
          internalStatusId: selected.value,
        });

        sessionStorage.removeItem('dailyStateCache');
        window.dispatchEvent(new Event('dailyStateUpdated'));
      } catch (err) {
        patchRow(key, previo);
        showSnackbar(err?.response?.data?.detail || t('cmu_manage_error'), 'error');
      } finally {
        setSavingRows((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [savingRows, patchRow, showSnackbar, t]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'fecha',
        label: t('cmu_col_date'),
        sort: true,
        render: (row) => (
          <span className='font-light small'>
            {formatDateByLocale(row.fecha, locale)}
          </span>
        ),
      },
      {
        accessor: 'tramite',
        label: t('cmu_col_process'),
        sort: true,
        render: (row) => <span>{row.tramite || '--'}</span>,
      },
      {
        accessor: 'descripcionTramite',
        label: t('cmu_col_description'),
        sort: true,
        render: (row) => <span>{row.descripcionTramite || '--'}</span>,
      },
      {
        accessor: 'internalStatusId',
        label: t('cmu_col_status'),
        sort: true,
        render: (row) => {
          const { label, color } = statusBadge(row);
          return (
            <div className='d-flex align-items-center'>
              <Badge text={label} color={color} size='sm' />
              {SvgIcon && row.nivelPrioridad > 0 && (
                <SvgIcon
                  name='exclamationTriangle'
                  size='1rem'
                  className='ms-2'
                  fill='var(--error-500)'
                  title={t('cmu_highPriority')}
                />
              )}
            </div>
          );
        },
      },
      {
        accessor: 'documentoId',
        label: t('cmu_col_document'),
        sort: true,
        render: (row) =>
          row.conDocumento &&
          row.documentoId && (
            <IconButton
              ariaLabel={t('cmu_viewDocument')}
              icon='fileEarmarkText'
              size='md'
              onClick={() => openDocument(row.documentoId)}
              disabled={!canViewDocument}
            />
          ),
      },
      {
        accessor: 'estadoDiarioId',
        label: t('cmu_col_actions'),
        render: (row) =>
          canManageMovements &&
          Dropdown && (
            <Dropdown
              ariaLabel={t('cmu_manage')}
              icon='threeDotsVertical'
              onSelect={(selected) => handleManage(selected, row)}
              options={statusOptions}
              variant='icon'
              disabled={savingRows.has(row.estadoDiarioId)}
            />
          ),
      },
    ],
    [
      Dropdown,
      SvgIcon,
      locale,
      statusBadge,
      openDocument,
      handleManage,
      statusOptions,
      savingRows,
      canViewDocument,
      canManageMovements,
      t,
    ]
  );

  const warnings = useMemo(() => {
    if (!meta) return [];
    return meta.degradadoDocumentos ? [t('cmu_warn_degradedDocuments')] : [];
  }, [meta, t]);

  return (
    <div className='just-table px-4'>
      {warnings.map((w) => (
        <p key={w} className='caption font-light mb-2'>
          {w}
        </p>
      ))}

      <Table
        columns={columns}
        data={data}
        paginator
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        paginatorGroup={PAGE_SIZES}
        loading={isLoading}
      />

      {!isLoading && error && (
        <div className='text-center p-4'>
          <p className='caption font-light mb-2'>{t('cmu_error')}</p>
          <Button
            label={t('cmu_retry')}
            variant='naked'
            size='sm'
            onClick={() => fetchMovements(caseId)}
          />
        </div>
      )}

      {!isLoading && !error && data.length === 0 && (
        <p className='text-center p-4 caption font-light'>{t('cmu_empty')}</p>
      )}
    </div>
  );
};

export default CaseMovementsUnholster;
